/**
 * 🧍 AvatarQuickLaunch — a compact avatar card that shows the user's
 * current avatar (or a friendly "Create yours" empty state) and links
 * to /avatar for full customization.
 *
 * Placed inside the AR bottom-sheet drawer on Interactive Gym & Kitchen
 * so users can actually FIND where to build / edit their avatar (owner
 * Feb 2026: "I have no idea where the avatar is on the interactive gym
 * or how to create them for my preferences").
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { AvatarPreview } from "./AvatarPreview";
import { ChevronRight, Sparkles, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AvatarQuickLaunch({ space = "gym" }) {
  const [avatar, setAvatar] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/avatar`, { withCredentials: true });
        if (mounted) setAvatar(r.data || null);
      } catch {
        if (mounted) setAvatar(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // API returns { physical, accessibility, style, exists } at the TOP
  // level (not wrapped in `.params`). Pass the whole doc as params.
  const hasAvatar = !!(avatar && (
    avatar.physical || avatar.accessibility || avatar.style
  ) && avatar.exists !== false);
  const isKitchen = space === "kitchen";
  const label = isKitchen ? "cook" : "train";
  // Tailwind can't purge dynamic template strings, so branch statically.
  const wrapperCls = isKitchen
    ? "border-amber-400/40 bg-gradient-to-r from-amber-950/40 to-black/60 hover:border-amber-300/70 hover:from-amber-900/50"
    : "border-sky-400/40 bg-gradient-to-r from-sky-950/40 to-black/60 hover:border-sky-300/70 hover:from-sky-900/50";
  const titleCls = isKitchen ? "text-amber-300" : "text-sky-300";
  const dashedCls = isKitchen ? "border-amber-400/50" : "border-sky-400/50";
  const plusCls = isKitchen ? "text-amber-300" : "text-sky-300";

  return (
    <a
      href="/avatar"
      className={`flex items-center gap-3 p-3 rounded-xl border transition group ${wrapperCls}`}
      data-testid={`avatar-quicklaunch-${space}`}
    >
      <div className="flex-shrink-0 relative">
        {loading ? (
          <div className="w-14 h-14 rounded-full bg-white/5 animate-pulse" />
        ) : hasAvatar ? (
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-white/20">
            <AvatarPreview params={avatar} size={56} />
          </div>
        ) : (
          <div className={`w-14 h-14 rounded-full border-2 border-dashed bg-black/40 flex items-center justify-center ${dashedCls}`}>
            <Plus size={22} className={plusCls} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-[10px] uppercase tracking-widest font-bold ${titleCls}`}>
          {hasAvatar ? "Your avatar" : "No avatar yet"}
        </div>
        <div className="text-sm font-bold text-white">
          {hasAvatar ? `Ready to ${label} with you` : `Build your avatar to ${label} alongside you`}
        </div>
        <div className="text-[10px] text-white/60 flex items-center gap-1 mt-0.5">
          <Sparkles size={9} className="text-amber-300" />
          {hasAvatar ? "Tap to edit body, hair, clothes, and preferences" : "Body, hair, clothes, and accessibility preferences"}
        </div>
      </div>
      <ChevronRight size={16} className="text-white/50 group-hover:text-white transition" />
    </a>
  );
}

export default AvatarQuickLaunch;
